import { useTranslation } from 'react-i18next';

import { BasePopover, Button } from '@hotjar/hotsauce';
import { useCurrentSite } from '@hotjar/site';
import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { noop } from '@hotjar/webapp/lib/function';
import { upsell } from '@hotjar/webapp/domains/revenue/upsell';
import { isAllowed } from '@hotjar/webapp/domains/common/feature';
import type { HeatmapParams } from '@hotjar/heatmaps';
import type { Site } from '@hotjar/site';
import { useHeatmapParams } from '@hotjar/webapp/domains/heatmaps/hooks/useHeatmapParams';
import { mapHeatmapParamsToGlobalFilters } from '@hotjar/heatmaps';
import { convertFromNewUiToQueryFormat } from '@hotjar/global-filters';

const createRecordingUrl = (
  params: HeatmapParams,
  context: {
    siteId: number;
    spaceId?: number;
  },
): string => {
  const pathname = `/sites/${context.siteId}/playbacks/list`;
  const mergedFilters = mapHeatmapParamsToGlobalFilters(params);
  const stringFilters = JSON.stringify(convertFromNewUiToQueryFormat(mergedFilters));
  const searchParams = new URLSearchParams({
    filters: stringFilters,
  });

  return new URL(`${pathname}?${searchParams}`, window.location.href).toString();
};

const ActiveRecordingsLink = ({ currentSite }: { currentSite: Site }) => {
  const { t } = useTranslation('heatmaps');
  const { click, getPermissionLevelForSite } = useTrack();
  const { params } = useHeatmapParams({
    siteId: currentSite.id,
  });
  const { coveragePercentage } = useTrafficCoverage();

  const link = createRecordingUrl(params, { siteId: currentSite.id });

  return (
    <Button
      as="a"
      href={link}
      kind="ghost"
      size="s"
      icon="play_alt"
      onClick={() =>
        click('view_recordings-icon', 'heatmap_session_count', {
          account_id: currentSite?.account.id,
          site_id: currentSite?.id,
          organization_id: currentSite?.organization.id,
          permission_level: getPermissionLevelForSite(currentSite),
          shown_traffic_coverage: coveragePercentage,
          state_event_was_triggered_on: 'heatmaps',
        })
      }
    >
      {t('view-recordings-link.link-text')}
    </Button>
  );
};

export const ViewRecordingsLink = (): JSX.Element | null => {
  const { t } = useTranslation('heatmaps');
  const { data: currentSite } = useCurrentSite();

  const isDisabled = currentSite ? !isAllowed('filters.visited_url', currentSite) : true;

  if (!currentSite) {
    return null;
  }

  if (isDisabled) {
    return (
      <BasePopover
        title={t('view-recordings-link.upsell-title')}
        content={<p>{t('view-recordings-link.upsell-content')}</p>}
        actionText={t('view-recordings-link.upsell-action')}
        trigger="mouseenter focus"
        withCloseButton={false}
        onActionPress={(e) => {
          e.stopPropagation();
          upsell({
            site: currentSite,
            referrer: 'analytics_product.filters.visited_url',
            additionalTrackingData: {
              state_event_was_triggered_on: 'heatmaps',
              ui_position: 'heatmap_session_count-view_recordings_of_this_page_popover_CTA',
            },
          }).then(noop, noop);
        }}
        appendTo="parent"
        placement="top-start"
      >
        <div>
          <Button kind="ghost" size="s" isDisabled icon="play_alt" color="text_disabled">
            {t('view-recordings-link.link-text')}
          </Button>
        </div>
      </BasePopover>
    );
  }

  return <ActiveRecordingsLink currentSite={currentSite} />;
};
